import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const DEFAULT_INPUT = `{
  "id": 42,
  "name": "Ada Lovelace",
  "isActive": true,
  "tags": ["engineer", "writer"],
  "address": {
    "street": "1 Analytical Way",
    "zip": null
  },
  "scores": [98, 99.5, 100]
}`;

const RESERVED = new Set([
  "break", "case", "catch", "class", "const", "continue", "debugger", "default",
  "delete", "do", "else", "enum", "export", "extends", "false", "finally", "for",
  "function", "if", "import", "in", "instanceof", "new", "null", "return",
  "super", "switch", "this", "throw", "true", "try", "typeof", "var", "void",
  "while", "with", "yield", "as", "implements", "interface", "let", "package",
  "private", "protected", "public", "static", "any", "boolean", "constructor",
  "declare", "get", "module", "require", "number", "set", "string", "symbol",
  "type", "from", "of",
]);

const isValidIdentifier = (key) =>
  /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key) && !RESERVED.has(key);

const pascalCase = (raw) => {
  if (!raw) return "Root";
  const parts = String(raw)
    .replace(/[^A-Za-z0-9]+/g, " ")
    .trim()
    .split(/\s+/);
  if (parts.length === 0) return "Root";
  const cased = parts
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join("");
  return /^[A-Za-z_$]/.test(cased) ? cased : `T${cased}`;
};

const singularize = (name) => {
  if (!name) return name;
  if (/ies$/i.test(name) && name.length > 3) return name.replace(/ies$/i, "y");
  if (/sses$/i.test(name)) return name.replace(/es$/i, "");
  if (/[^s]s$/i.test(name)) return name.replace(/s$/i, "");
  return name;
};

const primitiveType = (value) => {
  if (value === null) return "null";
  const t = typeof value;
  if (t === "string") return "string";
  if (t === "number") return Number.isFinite(value) ? "number" : "number";
  if (t === "boolean") return "boolean";
  return "unknown";
};

const mergeTypeSets = (a, b) => {
  const out = new Set(a);
  b.forEach((v) => out.add(v));
  return out;
};

const formatUnion = (set, optional) => {
  let parts = Array.from(set);
  if (parts.length === 0) return optional ? "undefined" : "unknown";
  const hasNull = parts.includes("null");
  parts = parts.filter((p) => p !== "null");
  parts.sort();
  if (hasNull) parts.push("null");
  return parts.join(" | ") || "unknown";
};

class TypeBuilder {
  constructor(opts) {
    this.opts = opts;
    this.interfaces = [];
    this.names = new Map();
  }

  uniqueName(base) {
    const name = pascalCase(base);
    const count = this.names.get(name) || 0;
    this.names.set(name, count + 1);
    return count === 0 ? name : `${name}${count + 1}`;
  }

  fromValue(value, hint) {
    if (Array.isArray(value)) return this.fromArray(value, hint);
    if (value !== null && typeof value === "object") {
      return this.fromObject(value, hint);
    }
    return primitiveType(value);
  }

  fromArray(arr, hint) {
    if (arr.length === 0) return "unknown[]";
    const childHint = singularize(hint) || "Item";
    const types = new Set();
    let objectShape = null;
    let sawObject = false;

    for (const item of arr) {
      if (item !== null && typeof item === "object" && !Array.isArray(item)) {
        sawObject = true;
        objectShape = objectShape
          ? this.mergeObjectShapes(objectShape, this.collectShape(item))
          : this.collectShape(item);
      } else if (Array.isArray(item)) {
        types.add(this.fromArray(item, childHint));
      } else {
        types.add(primitiveType(item));
      }
    }

    if (sawObject) {
      const ifaceName = this.uniqueName(childHint);
      this.emitInterface(ifaceName, objectShape);
      types.add(ifaceName);
    }

    const union = formatUnion(types, false);
    return types.size > 1 ? `(${union})[]` : `${union}[]`;
  }

  collectShape(obj) {
    const shape = new Map();
    for (const key of Object.keys(obj)) {
      shape.set(key, {
        types: new Set([this.describe(obj[key], key)]),
        optional: false,
      });
    }
    return shape;
  }

  mergeObjectShapes(a, b) {
    const out = new Map();
    const allKeys = new Set([...a.keys(), ...b.keys()]);
    for (const key of allKeys) {
      const inA = a.has(key);
      const inB = b.has(key);
      const aEntry = a.get(key) || { types: new Set(), optional: true };
      const bEntry = b.get(key) || { types: new Set(), optional: true };
      out.set(key, {
        types: mergeTypeSets(aEntry.types, bEntry.types),
        optional: aEntry.optional || bEntry.optional || !inA || !inB,
      });
    }
    return out;
  }

  describe(value, hint) {
    if (Array.isArray(value)) return this.fromArray(value, hint);
    if (value !== null && typeof value === "object") {
      const name = this.uniqueName(hint);
      const shape = this.collectShape(value);
      this.emitInterface(name, shape);
      return name;
    }
    return primitiveType(value);
  }

  fromObject(obj, hint) {
    const name = this.uniqueName(hint || "Root");
    const shape = this.collectShape(obj);
    this.emitInterface(name, shape);
    return name;
  }

  emitInterface(name, shape) {
    const lines = [];
    const keyword = this.opts.useType ? "type" : "interface";
    const opener = this.opts.useType ? `${keyword} ${name} = {` : `${keyword} ${name} {`;
    const closer = this.opts.useType ? "};" : "}";
    lines.push(opener);
    for (const [key, entry] of shape.entries()) {
      const safeKey = isValidIdentifier(key) ? key : JSON.stringify(key);
      const optional = entry.optional ? "?" : "";
      const union = formatUnion(entry.types, entry.optional);
      const readonly = this.opts.readonly ? "readonly " : "";
      lines.push(`  ${readonly}${safeKey}${optional}: ${union};`);
    }
    lines.push(closer);
    this.interfaces.push({ name, source: lines.join("\n") });
  }
}

const generate = (json, opts) => {
  const builder = new TypeBuilder(opts);
  const rootName = opts.rootName || "Root";
  if (Array.isArray(json)) {
    const arrayType = builder.fromArray(json, singularize(rootName));
    const decl = opts.useType
      ? `type ${pascalCase(rootName)} = ${arrayType};`
      : `type ${pascalCase(rootName)} = ${arrayType};`;
    return [...builder.interfaces.map((i) => i.source), decl].join("\n\n");
  }
  if (json !== null && typeof json === "object") {
    builder.fromObject(json, rootName);
    if (builder.interfaces.length > 0) {
      builder.interfaces[0].name = pascalCase(rootName);
      const head = builder.interfaces[0].source.split("\n");
      const keyword = opts.useType ? "type" : "interface";
      head[0] = opts.useType
        ? `${keyword} ${pascalCase(rootName)} = {`
        : `${keyword} ${pascalCase(rootName)} {`;
      builder.interfaces[0].source = head.join("\n");
    }
    return builder.interfaces.map((i) => i.source).join("\n\n");
  }
  const alias = primitiveType(json);
  return `type ${pascalCase(rootName)} = ${alias};`;
};

const JsonToTypescript = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState(DEFAULT_INPUT);
  const [rootName, setRootName] = useState("Root");
  const [useType, setUseType] = useState(false);
  const [readonly, setReadonly] = useState(false);

  const { output, error } = useMemo(() => {
    if (!input.trim()) return { output: "", error: "" };
    try {
      const parsed = JSON.parse(input);
      const code = generate(parsed, {
        rootName: rootName.trim() || "Root",
        useType,
        readonly,
      });
      return { output: code, error: "" };
    } catch (e) {
      return { output: "", error: e.message || String(e) };
    }
  }, [input, rootName, useType, readonly]);

  const stats = useMemo(() => {
    if (!output) return { lines: 0, interfaces: 0 };
    const lines = output.split(/\r?\n/).filter(Boolean).length;
    const interfaces = (output.match(/^(interface|type)\s/gm) || []).length;
    return { lines, interfaces };
  }, [output]);

  const copy = async () => {
    if (!output) {
      toast.error(t("json_to_ts_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const loadSample = () => setInput(DEFAULT_INPUT);
  const clear = () => setInput("");

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("json_to_ts_title")}
            </h1>
            <p className="text-gray-600">{t("json_to_ts_intro")}</p>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("json_to_ts_input_label")}
              </label>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("json_to_ts_input_placeholder")}
                rows={10}
                className="w-full p-2 font-mono text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("json_to_ts_root_name_label")}
                </label>
                <input
                  type="text"
                  value={rootName}
                  onChange={(e) => setRootName(e.target.value)}
                  placeholder="Root"
                  className="w-48 p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
                <span className="text-xs text-gray-500">
                  {t("json_to_ts_root_name_help")}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={useType}
                    onChange={(e) => setUseType(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("json_to_ts_use_type")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={readonly}
                    onChange={(e) => setReadonly(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("json_to_ts_readonly")}
                </label>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t("json_to_ts_output_label")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={loadSample}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("json_to_ts_sample")}
                  </StandardButton>
                  <StandardButton
                    onClick={clear}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!input}
                  >
                    {t("json_to_ts_clear")}
                  </StandardButton>
                  <StandardButton
                    onClick={copy}
                    type="primary"
                    size="small"
                    disabled={!output}
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>
              {error ? (
                <pre className="w-full min-h-[3rem] p-2 font-mono text-sm bg-red-50 border border-red-200 rounded-md whitespace-pre-wrap break-all text-red-700">
                  {t("json_to_ts_parse_error", { message: error })}
                </pre>
              ) : (
                <pre className="w-full min-h-[3rem] p-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md whitespace-pre-wrap break-all text-gray-800">
                  {output || (
                    <em className="text-gray-400 not-italic">
                      {t("json_to_ts_output_empty")}
                    </em>
                  )}
                </pre>
              )}
              <div className="text-xs text-gray-500">
                {output
                  ? t("json_to_ts_stats", {
                      lines: stats.lines,
                      interfaces: stats.interfaces,
                    })
                  : ""}
              </div>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("json_to_ts_note_inference")}</p>
              <p>{t("json_to_ts_note_optional")}</p>
              <p>{t("json_to_ts_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default JsonToTypescript;
